// Die Three.js-Szene als Ansicht der Welt: ein Mesh je Chunk aus `meshChunk`, ein Material mit
// dem Atlas, Himmel als Hintergrundfarbe, Nebel in derselben Farbe, Hemisphären- plus
// Richtungslicht ohne Schatten. Die Welt ist statisch; kein Spielzustand liegt in den Objekten
// (Architektur-Regel 1). Kamera und spätere Tiere werden von außen je Bild aus dem Zustand gesetzt.

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DirectionalLight,
  Fog,
  HemisphereLight,
  Mesh,
  MeshLambertMaterial,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  type Texture,
} from 'three';
import { CHUNK_SIZE, type World } from '@bauernhof/shared';
import { createAtlas } from './atlas.js';
import { meshChunk, type AtlasLayout } from './mesher.js';
import { SICHTWEITE, chunkImBlick, nebelBereich } from './sichtweite.js';

/** Himmelblau; Hintergrund und Nebel teilen sich die Farbe, damit die Ferne darin verschwindet. */
const HIMMEL = 0x8fc3ec;
/** Mehr Pixel als das 1,5-fache zahlen sich auf integrierter Grafik nicht aus. */
const PIXEL_RATIO_MAX = 1.5;
const FOV = 75;
const NAH = 0.1;
/**
 * Die Kamera reicht ein Stück über die Sichtweite hinaus: Was dahinter läge, ist ohnehin schon im
 * Nebel und wird nicht mehr gezeichnet (ADR 0025).
 */
const FERN = SICHTWEITE + 2 * CHUNK_SIZE;
/** Der Nebel wird genau vor der Sichtweite dicht, damit die Kante der Welt nicht auffällt. */
const NEBEL = nebelBereich(SICHTWEITE);

export interface SceneView {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  hemisphere: HemisphereLight;
  sun: DirectionalLight;
  /** Zahl der Chunk-Meshes in der Szene; die Sichtprüfung zeigt sie an. */
  readonly chunkCount: number;
  /**
   * Baut die Meshes dieser Chunks neu aus der Welt – etwa nachdem ein Tor auf- oder zugegangen ist
   * (task-013). Die alte Geometrie wird freigegeben; ein Chunk ohne Sichtbares verschwindet.
   */
  erneuereChunks(chunks: readonly { cx: number; cz: number }[]): void;
  render(): void;
}

export function createScene(canvas: HTMLCanvasElement, world: World): SceneView {
  const renderer = new WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, PIXEL_RATIO_MAX));

  const scene = new Scene();
  scene.background = new Color(HIMMEL);
  scene.fog = new Fog(HIMMEL, NEBEL.nah, NEBEL.fern);

  const camera = new PerspectiveCamera(FOV, 1, NAH, FERN);

  const hemisphere = new HemisphereLight(0xdfeeff, 0x6b5a3a, 1.0);
  scene.add(hemisphere);
  // Richtungslicht von Südost oben: Oberseiten am hellsten, Ost- und Südseiten heller als
  // Nord und West, damit die Blockkanten lesbar sind. Ohne Schatten (Abnahmekriterium).
  const sun = new DirectionalLight(0xfff1d6, 1.6);
  sun.position.set(0.4, 1, 0.25);
  scene.add(sun);

  const atlas = createAtlas();
  const material = createBlockMaterial(atlas.texture);
  // Ein Mesh je Chunk, dazu seine Chunk-Koordinaten: Je Bild wird daraus entschieden, welche
  // Chunks überhaupt gezeichnet werden. Bei 16 × 16 Chunks spart das den größten Teil der
  // Zeichenaufrufe, ohne dass etwas Sichtbares fehlt (ADR 0025).
  const chunkMeshes: { mesh: Mesh; cx: number; cz: number }[] = [];
  for (const chunk of world.chunks) {
    const mesh = createChunkMesh(world, chunk.cx, chunk.cz, atlas.layout, material);
    if (mesh === null) continue;
    scene.add(mesh);
    chunkMeshes.push({ mesh, cx: chunk.cx, cz: chunk.cz });
  }

  /** Blendet aus, was weiter weg ist als die Sichtweite; der Nebel deckt die Kante. */
  const setzeSichtweite = (): void => {
    const kamera = { x: camera.position.x, z: camera.position.z };
    for (const eintrag of chunkMeshes) {
      eintrag.mesh.visible = chunkImBlick(eintrag.cx, eintrag.cz, kamera, SICHTWEITE);
    }
  };

  const anpassen = (): void => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  anpassen();
  window.addEventListener('resize', anpassen);

  return {
    scene,
    camera,
    renderer,
    hemisphere,
    sun,
    get chunkCount() {
      return chunkMeshes.length;
    },
    erneuereChunks(chunks) {
      for (const { cx, cz } of chunks) {
        const index = chunkMeshes.findIndex((eintrag) => eintrag.cx === cx && eintrag.cz === cz);
        const alt = index === -1 ? undefined : chunkMeshes[index];
        if (alt !== undefined) {
          scene.remove(alt.mesh);
          alt.mesh.geometry.dispose();
          chunkMeshes.splice(index, 1);
        }
        const mesh = createChunkMesh(world, cx, cz, atlas.layout, material);
        if (mesh === null) continue;
        scene.add(mesh);
        chunkMeshes.push({ mesh, cx, cz });
      }
    },
    render() {
      setzeSichtweite();
      renderer.render(scene, camera);
    },
  };
}

/** Geometrie eines Chunks in Weltkoordinaten; null, wenn der Chunk nichts Sichtbares enthält. */
function createChunkMesh(world: World, cx: number, cz: number, layout: AtlasLayout, material: MeshLambertMaterial): Mesh | null {
  const daten = meshChunk(world, cx, cz, layout);
  if (daten.indices.length === 0) return null;
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(daten.positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(daten.normals, 3));
  geometry.setAttribute('uv', new BufferAttribute(daten.uvs, 2));
  geometry.setAttribute('kachel', new BufferAttribute(daten.tiles, 4));
  geometry.setIndex(new BufferAttribute(daten.indices, 1));
  geometry.computeBoundingSphere();
  const mesh = new Mesh(geometry, material);
  // Die Positionen sind bereits Weltkoordinaten; das Mesh bleibt am Ursprung und bewegt sich nie.
  mesh.matrixAutoUpdate = false;
  return mesh;
}

/**
 * Das Kachel-Stück für den Fragment-Shader: `vMapUv` liegt in Blockeinheiten, `fract` wickelt es
 * in das Kachel-Rechteck `vKachel` = [u0, v0, u1, v1]. So wiederholt sich eine Kachel über ein
 * Greedy-Rechteck beliebiger Größe, was `RepeatWrapping` bei einem Atlas nicht kann (ADR 0010).
 */
const KACHEL_MAP_FRAGMENT = /* glsl */ `
#ifdef USE_MAP
	vec2 kachelUv = vKachel.xy + fract( vMapUv ) * ( vKachel.zw - vKachel.xy );
	vec4 sampledDiffuseColor = texture2D( map, kachelUv );
	diffuseColor *= sampledDiffuseColor;
#endif
`;

/** Lambert-Material mit Atlas; `onBeforeCompile` reicht das Kachel-Attribut bis in den Fragment-Shader. */
function createBlockMaterial(texture: Texture): MeshLambertMaterial {
  const material = new MeshLambertMaterial({ map: texture });
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = ersetze(
      ersetze(shader.vertexShader, '#include <common>', '#include <common>\nattribute vec4 kachel;\nvarying vec4 vKachel;'),
      '#include <uv_vertex>',
      '#include <uv_vertex>\nvKachel = kachel;',
    );
    shader.fragmentShader = ersetze(
      ersetze(shader.fragmentShader, '#include <common>', '#include <common>\nvarying vec4 vKachel;'),
      '#include <map_fragment>',
      KACHEL_MAP_FRAGMENT,
    );
  };
  // Eigener Schlüssel, damit Three.js das Programm nicht mit einem unveränderten Lambert teilt.
  material.customProgramCacheKey = () => 'bauernhof-kachel-atlas';
  return material;
}

/** Ersetzt die erste Fundstelle; fehlt die Marke (etwa nach einem Three.js-Update), fällt es sofort auf. */
function ersetze(quelle: string, marke: string, ersatz: string): string {
  if (!quelle.includes(marke)) throw new Error(`Shader-Marke „${marke}“ nicht gefunden; Three.js hat seine Shader-Bausteine geändert.`);
  return quelle.replace(marke, ersatz);
}
